/**
 * «Наши проекты» в подвале зарубежных сайтов (ТЗ Seo-marketing 19.10.2026,
 * владелец: «давай сделаем между ними перелинковку»).
 *
 * На aisearch.tech, ingdg.com и datahunter.store до этого не было ни одной
 * ссылки друг на друга: молодые DataHunter и ingdg Google находил только
 * сам, а посетитель одного продукта не узнавал о соседних.
 *
 * Правила (схемы ссылок по Google):
 * - анкор — название бренда, а не «free dataset search tool»;
 * - обычные ссылки, без nofollow и без скрытия;
 * - на себя не ссылаемся.
 *
 * aisearch.ru не участвует: это российский контур, и с этих сайтов на него
 * не ведёт ни одна ссылка. Связь .ru ↔ .tech уже есть через hreflang.
 * Поэтому с русской версии страниц AiSearch и Конспект ведут на английскую
 * витрину .tech: .tech/ru закрыта от поиска как дубль .ru.
 *
 * Конспект с ingdg.com не показываем: у ingdg своя страница Конспекта и
 * своя покупка (`ingdg-konspekt-ssylki.ts`), они уже стоят в колонке
 * инструментов, и ссылка на тот же продукт на другом домене уводила бы
 * покупателя с сайта.
 */

export type Sajt = 'aisearch' | 'ingdg' | 'datahunter';

type Jazyk = 'en' | 'es' | 'sr' | 'ru';

interface Proekt {
  /** Чей это продукт: на этом сайте ссылку не показываем. */
  sajt: Sajt;
  nazvanie: string;
  /** Адрес английской версии, без косой черты в конце. */
  osnova: string;
  put: string;
  /** Языки, у которых своя версия по префиксу; остальные ведут на en. */
  jazyki: readonly Jazyk[];
  podpis: Record<Jazyk, string>;
}

const PROEKTY: readonly Proekt[] = [
  {
    sajt: 'aisearch',
    nazvanie: 'AiSearch',
    osnova: 'https://aisearch.tech',
    put: '',
    jazyki: ['en', 'es', 'sr'],
    podpis: {
      en: 'AI tools in your browser',
      es: 'Herramientas de IA en el navegador',
      sr: 'AI alati u pregledaču',
      ru: 'ИИ-инструменты в браузере',
    },
  },
  {
    sajt: 'aisearch',
    nazvanie: 'Konspekt',
    osnova: 'https://aisearch.tech',
    put: '/programs/konspekt',
    jazyki: ['en', 'es', 'sr'],
    podpis: {
      en: 'Meeting notes for Windows',
      es: 'Notas de reuniones para Windows',
      sr: 'Beleške sa sastanaka za Windows',
      ru: 'Конспекты встреч для Windows',
    },
  },
  {
    sajt: 'datahunter',
    nazvanie: 'DataHunter',
    osnova: 'https://datahunter.store',
    put: '',
    jazyki: ['en', 'es', 'sr', 'ru'],
    podpis: {
      en: 'Datasets and developer tools',
      es: 'Datasets y herramientas para desarrolladores',
      sr: 'Skupovi podataka i alati za programere',
      ru: 'Наборы данных и инструменты для разработчиков',
    },
  },
  {
    sajt: 'ingdg',
    nazvanie: 'INGDG',
    osnova: 'https://ingdg.com',
    put: '',
    jazyki: ['en', 'es', 'sr', 'ru'],
    podpis: {
      en: 'AI tools for business',
      es: 'Herramientas de IA para empresas',
      sr: 'AI alati za posao',
      ru: 'ИИ-инструменты для бизнеса',
    },
  },
];

export const ZAGOLOVOK_PROEKTOV: Record<Jazyk, string> = {
  en: 'Our projects',
  es: 'Nuestros proyectos',
  sr: 'Naši projekti',
  ru: 'Наши проекты',
};

export interface SsylkaProekta {
  nazvanie: string;
  podpis: string;
  href: string;
}

function jazykIli(locale: string): Jazyk {
  return locale === 'es' || locale === 'sr' || locale === 'ru' ? locale : 'en';
}

/** Ссылки блока для сайта `sajt` на странице языка `locale`. */
export function ssylkiProektov(sajt: Sajt, locale: string): SsylkaProekta[] {
  const jazyk = jazykIli(locale);
  return PROEKTY.filter((p) => p.sajt !== sajt)
    .filter((p) => !(sajt === 'ingdg' && p.nazvanie === 'Konspekt'))
    .map((p) => {
      const prefiks = jazyk !== 'en' && p.jazyki.includes(jazyk) ? `/${jazyk}` : '';
      const put = `${prefiks}${p.put}`;
      return {
        nazvanie: p.nazvanie,
        podpis: p.podpis[jazyk],
        href: put ? `${p.osnova}${put}` : p.osnova,
      };
    });
}

export function zagolovokProektov(locale: string): string {
  return ZAGOLOVOK_PROEKTOV[jazykIli(locale)];
}

/*
 * Ссылки в тексте (ТЗ Seo-marketing 19.10.2026, места сверены с ним):
 * только там, где соседний продукт и правда нужен человеку на этой
 * странице. Анкор — название инструмента или бренда, не ключевые слова.
 */

function prefiks(locale: string, jazyki: readonly string[]): string {
  return locale !== 'en' && jazyki.includes(locale) ? `/${locale}` : '';
}

/**
 * Страница Конспекта на aisearch.tech. Русской версии на .tech нет (она
 * закрыта от поиска как дубль aisearch.ru), туда — английская.
 */
export function adresKonspektaTech(locale: string): string {
  return `https://aisearch.tech${prefiks(locale, ['es', 'sr'])}/programs/konspekt`;
}

const VSE: readonly string[] = ['es', 'sr', 'ru'];

/** Часть фразы: текст или ссылка. */
export type ChastFrazy = string | { tekst: string; href: string };

const dh = (put: string) => (l: string) => `https://datahunter.store${prefiks(l, VSE)}${put}`;
const ingdg = (put: string) => (l: string) => `https://ingdg.com${prefiks(l, VSE)}${put}`;

/** Фраза с адресами для языка страницы. */
function sobrat(
  fraza: Record<Jazyk, (string | { tekst: string; href: (l: string) => string })[]>,
  locale: string,
): ChastFrazy[] {
  return fraza[jazykIli(locale)].map((c) =>
    typeof c === 'string' ? c : { tekst: c.tekst, href: c.href(locale) },
  );
}

const SEO_INGDG = ingdg('/tools/seo-checker');
const SSL_DH = dh('/app/ssl-checker');
const DNS_DH = dh('/app/dns-lookup');
const MIKROFON_DH = dh('/app/microphone-test');

/** DataHunter, проверка SSL и DNS → SEO-проверка INGDG. */
export function frazaSeoIngdg(locale: string): ChastFrazy[] {
  return sobrat(
    {
      en: [
        'Want to check the whole site, not just the domain? Try the ',
        { tekst: 'INGDG SEO checker', href: SEO_INGDG },
        '.',
      ],
      es: [
        '¿Quieres revisar todo el sitio y no solo el dominio? Prueba el ',
        { tekst: 'verificador SEO de INGDG', href: SEO_INGDG },
        '.',
      ],
      sr: [
        'Želite da proverite ceo sajt, a ne samo domen? Probajte ',
        { tekst: 'INGDG SEO proveru', href: SEO_INGDG },
        '.',
      ],
      ru: [
        'Нужно проверить весь сайт, а не только домен? Попробуйте ',
        { tekst: 'SEO-проверку INGDG', href: SEO_INGDG },
        '.',
      ],
    },
    locale,
  );
}

/** INGDG, SEO-проверка → проверка SSL и DNS на DataHunter. */
export function frazaSetDh(locale: string): ChastFrazy[] {
  return sobrat(
    {
      en: [
        'Need to check the SSL certificate or DNS records? DataHunter has free tools: ',
        { tekst: 'SSL checker', href: SSL_DH },
        ' and ',
        { tekst: 'DNS lookup', href: DNS_DH },
        '.',
      ],
      es: [
        '¿Necesitas revisar el certificado SSL o los registros DNS? DataHunter tiene herramientas gratuitas: ',
        { tekst: 'verificador SSL', href: SSL_DH },
        ' y ',
        { tekst: 'consulta DNS', href: DNS_DH },
        '.',
      ],
      sr: [
        'Treba da proverite SSL sertifikat ili DNS zapise? DataHunter ima besplatne alate: ',
        { tekst: 'provera SSL-a', href: SSL_DH },
        ' i ',
        { tekst: 'DNS pretraga', href: DNS_DH },
        '.',
      ],
      ru: [
        'Нужно проверить SSL-сертификат или DNS-записи? У DataHunter есть бесплатные инструменты: ',
        { tekst: 'проверка SSL', href: SSL_DH },
        ' и ',
        { tekst: 'DNS-запросы', href: DNS_DH },
        '.',
      ],
    },
    locale,
  );
}

/** Конспект на aisearch.tech → проверка микрофона на DataHunter. */
export function frazaMikrofonDh(locale: string): ChastFrazy[] {
  return sobrat(
    {
      en: [
        'Test your microphone before the first recording: ',
        { tekst: 'DataHunter microphone test', href: MIKROFON_DH },
        '.',
      ],
      es: [
        'Prueba tu micrófono antes de la primera grabación: ',
        { tekst: 'prueba de micrófono de DataHunter', href: MIKROFON_DH },
        '.',
      ],
      sr: [
        'Proverite mikrofon pre prvog snimanja: ',
        { tekst: 'DataHunter provera mikrofona', href: MIKROFON_DH },
        '.',
      ],
      ru: [
        'Проверьте микрофон перед первой записью: ',
        { tekst: 'проверка микрофона DataHunter', href: MIKROFON_DH },
        '.',
      ],
    },
    locale,
  );
}
